<div class="editor-container">
  <app-loading-spinner *ngIf="!imgSrc.base64"></app-loading-spinner>

  <h1 mat-dialog-title>Image Editor</h1>

  <!-- Hidden canvas for rendering the final image -->
  <canvas #canvas style="display: none;"></canvas>

  <mat-dialog-content>
    <div class="editor-layout">
      <!-- Left side: Image Preview -->
      <div class="image-preview-container" [style.maxWidth.px]="maxImageDimensions.width" [style.maxHeight.px]="maxImageDimensions.height">
        <image-cropper
          [imageBase64]="imgSrc.base64 ? imgSrc.base64.toString() : null"
          [maintainAspectRatio]="true"
          [aspectRatio]="cropperAspectRatio"
          [containWithinAspectRatio]="true"
          [transform]="imgTransform"
          [canvasRotation]="canvasRotation"
          [resizeToWidth]="maxImageDimensions.width"
          [resizeToHeight]="maxImageDimensions.height"
          [onlyScaleDown]="true"
          [roundCropper]="false"
          [alignImage]="'center'"
          style="max-width: 100% !important; max-height: 100%; padding: 0;"
          (imageLoaded)="imageLoaded()"
          (loadImageFailed)="loadImageFailed()"
          (cropperReady)="cropperReady()"
          (imageCropped)="imageCropped($event)"
          [style.filter]="getCurrentFilters()"
          class="cropper">
        </image-cropper>
        
        <!-- Text Overlay - Always visible when text is entered -->
        <div *ngIf="textOverlay.text.trim()" 
             class="text-overlay" 
             [style.top.%]="textOverlay.y"
             [style.left.%]="textOverlay.x"
             [style.fontSize.px]="textOverlay.fontSize"
             [style.fontFamily]="textOverlay.fontFamily"
             [style.color]="textOverlay.color"
             cdkDragBoundary=".image-preview-container"
             cdkDrag>
          {{textOverlay.text}}
        </div>
      </div>
      
      <!-- Right side: All Editing Tools in a single panel -->
      <div class="editing-tools-container">
        <div class="tool-section">
          <!-- Rotation controls -->
          <h3>Rotation</h3>
          <div class="tool-buttons">
            <button mat-icon-button color="primary" (click)="rotate90CounterClockwise()" matTooltip="Rotate left">
              <mat-icon>rotate_left</mat-icon>
            </button>
            <button mat-icon-button color="primary" (click)="rotate90Clockwise()" matTooltip="Rotate right">
              <mat-icon>rotate_right</mat-icon>
            </button>
          </div>
          
          <!-- Image Adjustments -->
          <h3>Adjustments</h3>
          <div class="adjustment-sliders">
            <div class="slider-container">
              <span class="slider-label">Brightness</span>
              <mat-slider min="-100" max="100" step="1" [(ngModel)]="adjustments.brightness" (input)="onAdjustmentChange()" color="primary" class="full-width"></mat-slider>
              <div class="slider-value">{{adjustments.brightness}}</div>
            </div>
            
            <div class="slider-container">
              <span class="slider-label">Contrast</span>
              <mat-slider min="-100" max="100" step="1" [(ngModel)]="adjustments.contrast" (input)="onAdjustmentChange()" color="primary" class="full-width"></mat-slider>
              <div class="slider-value">{{adjustments.contrast}}</div>
            </div>
            
            <div class="slider-container">
              <span class="slider-label">Saturation</span>
              <mat-slider min="-100" max="100" step="1" [(ngModel)]="adjustments.saturation" (input)="onAdjustmentChange()" color="primary" class="full-width"></mat-slider>
              <div class="slider-value">{{adjustments.saturation}}</div>
            </div>
          </div>

          <!-- Filters -->
          <h3>Filters</h3>
          <div class="filters-grid">
            <div class="filter-item" 
                 [class.selected]="currentFilter === filter.name" 
                 (click)="applyFilter(filter.name)"
                 *ngFor="let filter of availableFilters">
              <div class="filter-preview" 
                   [style.background-image]="'url(' + previewImageUrl + ')'" 
                   [style.filter]="filter.style">
              </div>
              <div class="filter-name">{{filter.name}}</div>
            </div>
          </div>

          <!-- Text Overlay - Always available -->
          <h3>Add Text</h3>
          <div class="text-controls">
            <mat-form-field class="full-width">
              <input matInput placeholder="Enter your text" [(ngModel)]="textOverlay.text" (input)="updateTextOverlay()">
            </mat-form-field>
            
            <div class="text-control-row">
              <div class="text-control-col">
                <span class="slider-label">Font Size</span>
                <mat-slider min="12" max="72" step="1" [(ngModel)]="textOverlay.fontSize" (input)="updateTextOverlay()" color="primary" class="full-width"></mat-slider>
                <div class="slider-value">{{textOverlay.fontSize}}px</div>
              </div>
            </div>
            
            <div class="text-control-row">
              <div class="text-control-col">
                <mat-form-field class="full-width">
                  <mat-label>Font Family</mat-label>
                  <mat-select [(ngModel)]="textOverlay.fontFamily" (selectionChange)="updateTextOverlay()">
                    <mat-option value="Arial">Arial</mat-option>
                    <mat-option value="Verdana">Verdana</mat-option>
                    <mat-option value="Times New Roman">Times New Roman</mat-option>
                    <mat-option value="Georgia">Georgia</mat-option>
                    <mat-option value="Courier New">Courier New</mat-option>
                  </mat-select>
                </mat-form-field>
              </div>
              <div class="text-control-col">
                <span class="slider-label">Text Color</span>
                <input type="color" [(ngModel)]="textOverlay.color" (input)="updateTextOverlay()" class="color-picker">
              </div>
            </div>
            
            <div class="guide-text">
              <small>Drag the text to position it anywhere on the image</small>
            </div>
          </div>

          <div class="action-row">
            <button mat-stroked-button color="warn" (click)="resetAllChanges()" matTooltip="Reset all changes">
              <mat-icon>restart_alt</mat-icon> Reset All
            </button>
          </div>
        </div>
      </div>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions class="actions">
    <button mat-button matDialogClose matTooltip="Cancel and discard changes">Cancel</button>
    <button mat-raised-button 
            color="primary" 
            (click)="processEditAndCloseDialog()" 
            [disabled]="!isImageReady || isProcessing"
            matTooltip="{{!isImageReady ? 'Please wait for the image to load' : isProcessing ? 'Processing...' : 'Apply changes and continue'}}">
      {{isProcessing ? 'Processing...' : 'Apply'}}
    </button>
  </mat-dialog-actions>
</div>
